
import { useEffect, useState } from "react";
import { useInshurLanding } from "../landingMode";
import Image from "./Image";
import { SmartLink as Link } from "./SmartLink";
import { BookOpen, Sparkles, X } from "lucide-react";
import { AboutRentidDialog } from "./AboutRentid";
import { PRODUCTS } from "./productNavData";
export const ACCENT = "#33F7BE";
/* Ask Rentii (the AI chat) is off until the site has a chat backend: it needs the `ai` and
   `@ai-sdk/react` packages and an /api/rentii serverless function with an AI provider key. The
   ported chat panel is kept, unimported, in RentiiChatPanel.jsx. To turn it on: add the backend,
   import ChatPanel from there, and set this to true. */
const RENTII_CHAT_ENABLED = false;
/* The "r" glyph from the Rentid wordmark, drawn as a brand-green outline. */
function RentidROutline({ className, color = ACCENT }) {
    return (<svg viewBox="-1.5 7.3 19 28.5" fill="none" aria-hidden="true" className={className}>
      <path d="M6.54887 9.48108V14.0751H6.6466C6.98871 13.2931 7.37969 12.6089 7.91728 11.9247C8.45488 11.2894 9.09022 10.7029 9.77444 10.263C10.4586 9.82319 11.2406 9.43221 12.0225 9.18785C12.8045 8.94349 13.6353 8.79688 14.515 8.79688C14.9549 8.79688 15.4436 8.89462 15.9812 9.04124V15.3457C15.6391 15.2969 15.297 15.248 14.8083 15.1991C14.3684 15.1503 13.9286 15.1503 13.5376 15.1503C12.3158 15.1503 11.2406 15.3457 10.3609 15.7856C9.48119 16.1766 8.79699 16.763 8.25939 17.4961C7.7218 18.2292 7.37969 19.06 7.13533 19.9886C6.89096 20.966 6.79323 21.9924 6.79323 23.1164V34.2593H0V9.48108H6.54887Z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    </svg>);
}
/* The Ask Rentii + Toolbar buttons that sit at the foot of the left rail. */
export function RentiiRailButtons({ panel, onToggle, onLight = false, }) {
    // "About Rentid" (Vish, 2026-10-01): a book button opening the Rentid story dialog.
    const [aboutOpen, setAboutOpen] = useState(false);
    return (<>
      <DockButton label="About Rentid" active={aboutOpen} hideTip={panel !== null} onClick={() => setAboutOpen(true)} primary>
        <BookOpen className="h-4 w-4 transition-colors duration-300" stroke={onLight ? "#FFFFFF" : ACCENT} aria-hidden="true"/>
      </DockButton>
      <AboutRentidDialog open={aboutOpen} onOpenChange={setAboutOpen}/>
      {RENTII_CHAT_ENABLED && (<DockButton label="Ask Rentii" active={panel === "chat"} hideTip={panel !== null} onClick={() => onToggle("chat")} primary>
        <svg width="0" height="0" className="absolute" aria-hidden="true" focusable="false">
            <defs>
              <linearGradient id="rentii-ai-gradient" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
 <stop offset="0%" stopColor="#34D399"/>
 <stop offset="22%" stopColor="#3B82F6"/>
 <stop offset="60%" stopColor="#6366F1"/>
 <stop offset="100%" stopColor="#A855F7"/>
              </linearGradient>
            </defs>
          </svg>
          <Sparkles className="h-4 w-4 transition-colors duration-300" stroke={onLight ? "#FFFFFF" : "url(#rentii-ai-gradient)"} aria-hidden="true"/>
      </DockButton>)}
      <DockButton label="Product shortcuts" active={panel === "toolbar"} hideTip={panel !== null} onClick={() => onToggle("toolbar")}>
        <RentidROutline className="h-4 w-4" color={onLight ? "#FFFFFF" : ACCENT}/>
      </DockButton>
    </>);
}
export function RentiiRailPanel({ panel, onClose }) {
    useEffect(() => {
        if (!panel)
            return;
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [panel, onClose]);
    if (!panel)
        return null;
    return (<div className="absolute left-full top-1/2 ml-4 -translate-y-1/2">
      <ToolbarPanel onClose={onClose}/>
    </div>);
}
function DockButton({ label, active, hideTip, primary, onClick, children, }) {
    return (<button type="button" onClick={onClick} aria-label={label} aria-expanded={active} className={`group relative flex h-9 w-9 items-center justify-center rounded-full outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-white/50 ${active || primary ? "border border-white/15 bg-white/10" : "hover:bg-white/10"} ${active ? "ring-1 ring-white/25" : ""}`}>
      {children}
      {!hideTip && (<span className="pointer-events-none absolute left-full top-1/2 ml-3 flex -translate-x-1 -translate-y-1/2 items-center whitespace-nowrap rounded-xl border border-white/10 bg-black px-3 py-1.5 text-sm font-medium text-white opacity-0 shadow-xl transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100">
          <span className="absolute right-full top-1/2 h-0 w-0 -translate-y-1/2 border-y-[7px] border-r-[7px] border-y-transparent border-r-black"/>
          {label}
        </span>)}
    </button>);
}
export const panelShell = "flex flex-col rounded-3xl border border-white/10 bg-neutral-950/85 p-5 text-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] ring-1 ring-white/5 backdrop-blur-2xl backdrop-saturate-150";
function ToolbarPanel({ onClose }) {
    const inshur = useInshurLanding(); // /inshur: in-page shortcuts only
    return (<section role="dialog" aria-label="Product shortcuts" className={`${panelShell} w-[min(340px,calc(100vw-7rem))] gap-3`}>
      <header className="flex items-center justify-between border-b border-white/10 pb-3 pl-2">
        <span className="inline-flex items-center gap-2.5 text-sm font-semibold">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/5">
            <RentidROutline className="h-3.5 w-3.5"/>
          </span>
          Product shortcuts
        </span>
        <button type="button" onClick={onClose} aria-label="Close shortcuts" className="rounded-full p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white">
          <X className="h-4 w-4"/>
        </button>
      </header>
      <ul className="flex flex-col gap-1">
        {PRODUCTS.map((p) => (<li key={p.anchor}>
            <a href={`#${p.anchor}`} onClick={onClose} className="flex items-center gap-3 rounded-2xl px-2 py-2 transition-colors hover:bg-white/10">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5">
                <Image src={p.mark.src} alt="" width={p.mark.width} height={p.mark.height} unoptimized className="h-6 w-6 object-contain"/>
              </span>
              <span className="flex flex-col">
                <span className="text-sm font-semibold">{p.name}</span>
                <span className="text-xs text-white/55">{p.tagline}</span>
              </span>
            </a>
          </li>))}
      </ul>
      {/* Owner (2026-10-01): Pricing scrolls to the plans section on both pages. */}
      <div className="flex gap-2 border-t border-white/10 pt-3">
        <button type="button" onClick={() => { const plans = document.getElementById("plans"); if (plans) plans.scrollIntoView({ behavior: "smooth", block: "start" }); onClose(); }} className="flex-1 rounded-full border border-white/15 px-4 py-2 text-center text-sm font-medium transition-colors hover:bg-white/10">
          Pricing
        </button>
        {!inshur && (<Link href="https://app.rentid.io/sign-up" className="flex-1 rounded-full px-4 py-2 text-center text-sm font-semibold text-black" style={{ backgroundColor: ACCENT }}>
          Start for free
        </Link>)}
      </div>
    </section>);
}
